import { Lifecycle } from './Lifecycle.ts'
import { Token } from '../js/Token.js'
import {
  AgentToolRuntime,
  AggregateToolRuntime,
  type ChatToolCall,
  type ClientToolDescriptor,
  GatewayToolRuntime,
  type JSONRecord,
  type SessionToolRuntime,
  type ToolResult,
} from './McpAgentTools.ts'
import {
  JsonRpcMcpServer,
  type JsonRpcMcpServerOptions,
  type McpContext,
  type McpToolArguments,
  type McpToolProvider,
  ProtocolError,
} from './McpServer.ts'
import {
  appendAssistantEvent,
  appendToolResultEvent,
  type GenerativeChatMessage,
  type GenerativeChatRenderEvent,
  type GenerativeChatStreamFrame,
  historyToRenderEvents,
  RENDER_STREAM_CONTENT_TYPE,
  RenderEventStream,
  toolCallMessage,
} from './GenerativeChatEvents.ts'
import { hasMcpToolCapability, OAuthMcpToken } from './McpToolAuthorization.ts'
import type { McpCapabilityToken } from './McpToolAuthorization.ts'
import { response } from './Responses.ts'
import {
  CONTINUATION_HEADER,
  COUNTERPARTY_CONTEXT_HEADER,
  OAUTH_CONTEXT_HEADER,
} from './Requests.ts'
import type { JSONValue } from './Assertions.ts'
import type { YamlContent } from './YamlContent.ts'
import { getAppId } from '../js/Digest.js'

const DEFAULT_MAX_TOOL_ROUNDS = 4
const DEFAULT_TEMPERATURE = 0.2
const TOKEN_HEADER = Token.TOKEN_HEADER
const EXPERT_ASK = 'expert_ask'
const EXPERT_ASK_CAPABILITY = 'expertAsk'

/**
 * A party whose tools this chat may draw on. `tools` is an optional allow-list
 * of tool names (from that provider's catalog) to expose; omit it to expose all
 * of the provider's tools.
 */
export interface ToolProviderSpec {
  party: string
  tools?: string[]
  /**
   * Scope to claim in the token used to reach this provider's tools. Defaults to
   * the agent's `targetScope`. Set a narrower scope for a cross-party provider
   * that only grants the caller a subset (claiming an ungranted capability is
   * rejected wholesale by the provider).
   */
  scope?: string
}

export interface GenerativeChatAgentOptions {
  targetScope: string
  defaultSystemPrompt: string
  defaultTemperature?: number
  /** Initial value for the extended-thinking setting. Defaults to false. */
  defaultThinking?: boolean
  /**
   * Model id every chat this agent creates is pinned to, unless a per-session
   * `model` is supplied (e.g. by the browser at chat creation). Undefined keeps
   * the deployment default. Applied to both the browser flow and `expert_ask`.
   */
  defaultModel?: string
  maxToolRounds?: number
  mcpPath?: string
  /**
   * Resolves the set of parties whose tools this chat may use. Returning
   * undefined (or an empty list) keeps the default single-party behavior: all of
   * the target party's own tools. The same initiator forward context is applied
   * to every provider, so each authorizes the tool call as the counterparty.
   */
  resolveToolProviders?: (
    targetParty: string,
    forward: ForwardContext,
  ) => Promise<ToolProviderSpec[] | undefined>
  /**
   * Resolves the owner's memory tick selection: the `(root, collection)` keys
   * (see the platform's `memorySelectionKey`) a chat may surface from the target
   * party's own library. Returning undefined keeps the default behavior — every
   * permitted memory is surfaced. Applied to both memory-catalog surfaces (the
   * system-prompt inventory and the `memory_catalog` tool), and only to the
   * agent's own party, where its memories live. A soft surface filter: the
   * counterparty permission boundary is enforced separately by the platform.
   */
  resolveMemorySelection?: (
    targetParty: string,
    forward: ForwardContext,
  ) => Promise<string[] | undefined>
  /**
   * Resolves the owner's structured-memory tick selection — the structured twin
   * of {@link resolveMemorySelection}. Applied identically (the structured
   * system-prompt inventory and the `kind: 'structured'` memory-catalog tool
   * calls) and only to the agent's own party.
   */
  resolveStructuredMemorySelection?: (
    targetParty: string,
    forward: ForwardContext,
  ) => Promise<string[] | undefined>
}

/**
 * Identity to forward to the target party so it authorizes tool/memory use as
 * the connecting counterparty (not the agent). `oauthContext` carries an OAuth
 * client; `counterpartyContext` carries a signed-token peer as `{counterparty,
 * src}`. The target intersects whichever is present with the agent's own grants.
 */
interface ForwardContext {
  oauthContext: string | null
  counterpartyContext: string | null
  // Continuation token forwarded on cross-party gateway calls so the callee can
  // group repeated calls into one downstream session. Defaults to the active
  // session's chat id; an inbound value is passed through unchanged so multi-hop
  // chains preserve the originating token.
  continuation: string | null
}

interface GenerativeChatSession {
  chatId: string
  targetParty: string
  forward: ForwardContext
  toolRuntime: SessionToolRuntime
  clientTools: ClientToolDescriptor[]
  clientToolNames: Set<string>
  pendingClientToolCalls: Map<string, ChatToolCall>
  pendingToolResults: ToolResult[]
  toolRounds: number
}

interface GenerativeChatSettings {
  systemPrompt?: string
  temperature: number
  toolsEnabled: boolean
  toolDetails: boolean
  thinking: boolean
}

interface SemanticMemoryPromptResource {
  root?: unknown
  collection?: unknown
  description?: unknown
  schema?: unknown
}

export interface GenerativeChatConversationResult {
  events: GenerativeChatRenderEvent[]
  clientToolCalls?: ChatToolCall[]
}

export interface GenerativeChatMcpContext extends McpContext {
  party: string
  source: string
  protocol: string
  sourcePrefix: string
  yaml?: YamlContent
  token: Token | McpCapabilityToken | null
  request: Request
}

export class GenerativeChatAgent {
  #targetScope: string
  #defaultSystemPrompt: string
  #defaultTemperature: number
  #defaultModel?: string
  #maxToolRounds: number
  #mcpPath: string
  #resolveToolProviders?: GenerativeChatAgentOptions['resolveToolProviders']
  #resolveMemorySelection?: GenerativeChatAgentOptions['resolveMemorySelection']
  #resolveStructuredMemorySelection?: GenerativeChatAgentOptions['resolveStructuredMemorySelection']
  #sessions = new Map<string, GenerativeChatSession>()
  // Maps a reuse key (verified caller identity + opaque continuation token, see
  // `expertReuseKey`) to the bar-side chat id, so repeated `expert_ask` calls
  // from the same initiating chat continue one growing chat instead of creating
  // a fresh one each time. In-memory; lost on daemon restart (then reuse starts
  // a new chat — acceptable).
  #expertChats = new Map<string, string>()
  #lifecycle = new Lifecycle()
  #settings: GenerativeChatSettings
  #mcp: JsonRpcMcpServer<GenerativeChatMcpContext>

  constructor(options: GenerativeChatAgentOptions) {
    this.#targetScope = options.targetScope
    this.#defaultSystemPrompt = options.defaultSystemPrompt
    this.#defaultTemperature = options.defaultTemperature ??
      DEFAULT_TEMPERATURE
    this.#defaultModel = options.defaultModel
    this.#maxToolRounds = options.maxToolRounds ?? DEFAULT_MAX_TOOL_ROUNDS
    this.#mcpPath = options.mcpPath ?? '/rpc'
    this.#resolveToolProviders = options.resolveToolProviders
    this.#resolveMemorySelection = options.resolveMemorySelection
    this.#resolveStructuredMemorySelection = options.resolveStructuredMemorySelection
    this.#settings = {
      temperature: this.#defaultTemperature,
      toolsEnabled: true,
      toolDetails: true,
      thinking: options.defaultThinking ?? false,
    }
    this.#mcp = new JsonRpcMcpServer(
      new GenerativeChatExpertTools(this),
      {
        context: lifecycleContext,
        serverInfo: {
          name: 'ai-chat-expert',
          version: '1',
        },
      } satisfies JsonRpcMcpServerOptions<GenerativeChatMcpContext>,
    )
  }

  async handler(request: Request): Promise<Response> {
    if (Lifecycle.shouldHandle(request)) {
      return await this.#lifecycle.handler(request)
    }

    try {
      if (request.method !== 'POST') throw '405 Method Not Allowed'

      const url = new URL(request.url)
      if (url.pathname === this.#mcpPath) {
        return await this.#mcp.handle(request)
      }

      switch (url.pathname) {
        case '/session':
          return response({ ok: await this.#getSession(request) })

        case '/settings':
          return response({ ok: await this.#getSettings(request) })

        case '/models':
          return response({ ok: await this.#models(request) })

        case '/chats':
          return response({ ok: await this.#listChats(request) })

        case '/send':
          return this.#streamingRequested(request)
            ? this.#streamConversation((events) => this.#send(request, events))
            : response({ ok: await this.#send(request, []) })

        case '/client-tool-results':
          return this.#streamingRequested(request)
            ? this.#streamConversation((events) =>
              this.#clientToolResults(request, events))
            : response({ ok: await this.#clientToolResults(request, []) })

        case '/close':
          return response({ ok: await this.#close(request) })

        case '/delete':
          return response({ ok: await this.#deleteChat(request) })

        default:
          throw '404 Not Found'
      }
    } catch (e) {
      console.error(e)
      return response({ error: String(e) })
    }
  }

  /** A caller opts into per-turn streaming with `Accept: application/x-ndjson`. */
  #streamingRequested(request: Request): boolean {
    return request.headers.get('Accept')?.includes(RENDER_STREAM_CONTENT_TYPE) ??
      false
  }

  /**
   * Run a conversation turn, streaming each render event to the client as an
   * NDJSON `event` frame the moment it is produced (the conversation mutates the
   * injected {@link RenderEventStream} in place). A trailing `done` frame carries
   * any pending `clientToolCalls`; an error escaping the run (e.g. session setup,
   * before {@link #withPartialEvents} can fold it into the trail) becomes an
   * `error` frame, since the 200 response is already committed.
   */
  #streamConversation(
    run: (
      events: GenerativeChatRenderEvent[],
    ) => Promise<GenerativeChatConversationResult>,
  ): Response {
    const encoder = new TextEncoder()
    const stream = new ReadableStream<Uint8Array>({
      start: async (controller) => {
        const write = (frame: GenerativeChatStreamFrame) =>
          controller.enqueue(encoder.encode(`${JSON.stringify(frame)}\n`))
        const events = new RenderEventStream((event) =>
          write({ type: 'event', event }))
        try {
          const result = await run(events)
          write({
            type: 'done',
            ...(result.clientToolCalls?.length
              ? { clientToolCalls: result.clientToolCalls }
              : {}),
          })
        } catch (e) {
          console.error(e)
          write({ type: 'error', message: String(e) })
        } finally {
          controller.close()
        }
      },
    })
    return new Response(stream, {
      status: 200,
      headers: {
        'Content-Type': RENDER_STREAM_CONTENT_TYPE,
        'Cache-Control': 'no-cache',
        'Access-Control-Allow-Origin': '*',
      },
    })
  }

  async askExpert(
    request: Request,
    question: string,
  ): Promise<{ answer: string }> {
    const trimmed = question.trim()
    if (!trimmed) throw 'Question is required'

    const targetParty = this.#normalizeTargetParty(undefined)
    const forward = forwardContextFrom(request)
    const systemPrompt = await this.#systemPromptForChat(targetParty, {
      expert: true,
    }, forward)
    const temperature = this.#settings.temperature

    // When the initiating chat advertises a continuation token, group repeated
    // asks into one persistent bar chat keyed by the *verified* caller identity.
    // No token (or no verifiable identity) keeps the stateless contract below.
    const continuation = request.headers.get(CONTINUATION_HEADER)
    const reuseKey = continuation ? expertReuseKey(request, continuation) : null
    if (reuseKey) {
      return await this.#askExpertReusing(
        request,
        targetParty,
        forward,
        reuseKey,
        systemPrompt,
        temperature,
        trimmed,
      )
    }

    // Stateless contract: a fresh chat created and closed for this one answer.
    const session = await this.#sessionForRequest(
      request,
      targetParty,
      undefined,
      systemPrompt,
      temperature,
    )
    try {
      return { answer: await this.#askOnSession(session, trimmed, temperature) }
    } finally {
      this.#sessions.delete(
        sessionKey(request, session.targetParty, session.chatId),
      )
      await this.#postParty(session.targetParty, '/ai/chat/close', {
        chatId: session.chatId,
      }, session.forward).catch((e) => console.error(e))
    }
  }

  /**
   * The reuse path for `askExpert`: continue the bar chat mapped to `reuseKey`
   * if one exists, otherwise create one and remember it. The chat is never
   * closed here — it persists so the next ask from the same initiating chat
   * continues it. Self-heals if the mapped chat was closed out-of-band.
   */
  async #askExpertReusing(
    request: Request,
    targetParty: string,
    forward: ForwardContext,
    reuseKey: string,
    systemPrompt: string,
    temperature: number,
    message: string,
  ): Promise<{ answer: string }> {
    const reuseChatId = this.#expertChats.get(reuseKey)
    try {
      const session = await this.#sessionForRequest(
        request,
        targetParty,
        reuseChatId,
        systemPrompt,
        temperature,
      )
      if (!reuseChatId) this.#expertChats.set(reuseKey, session.chatId)
      return { answer: await this.#askOnSession(session, message, temperature) }
    } catch (e) {
      // Self-heal only when an actually-reused chat was closed out-of-band:
      // drop the stale mapping, create a fresh chat once, and retry.
      if (reuseChatId && await this.#chatClosed(targetParty, reuseChatId, forward)) {
        this.#expertChats.delete(reuseKey)
        this.#sessions.delete(sessionKey(request, targetParty, reuseChatId))
        const session = await this.#sessionForRequest(
          request,
          targetParty,
          undefined,
          systemPrompt,
          temperature,
        )
        this.#expertChats.set(reuseKey, session.chatId)
        return { answer: await this.#askOnSession(session, message, temperature) }
      }
      throw e
    } finally {
      // Bound in-memory growth without closing the persistent chat: drop the
      // transient session entry but keep the #expertChats mapping.
      const chatId = this.#expertChats.get(reuseKey)
      if (chatId) this.#sessions.delete(sessionKey(request, targetParty, chatId))
    }
  }

  /**
   * Runs a single expert exchange on `session`: resets per-ask state (a reused
   * session may carry stale pending tools/rounds), sends the message, drives any
   * server tool rounds, and returns the assistant's answer.
   */
  async #askOnSession(
    session: GenerativeChatSession,
    message: string,
    temperature: number,
  ): Promise<string> {
    clearPendingClientTools(session)
    session.toolRounds = 0
    await this.#refreshToolCatalog(session)
    const events: GenerativeChatRenderEvent[] = []

    const result = await this.#postParty(
      session.targetParty,
      '/ai/chat/send',
      {
        chatId: session.chatId,
        message,
        options: this.#sendOptions(
          session,
          this.#settings.toolsEnabled,
          temperature,
        ),
      },
      session.forward,
    )
    const conversation = await this.#continueConversation(
      session,
      result.message as GenerativeChatMessage,
      this.#settings.toolsEnabled,
      false,
      temperature,
      events,
    )
    if (conversation.clientToolCalls?.length) {
      throw 'Expert MCP cannot execute browser client tools'
    }
    const answer = latestAssistantAnswer(conversation.events)
    if (!answer) throw 'Expert did not return an answer'
    return answer
  }

  async #send(
    request: Request,
    events: GenerativeChatRenderEvent[],
  ): Promise<GenerativeChatConversationResult> {
    const body = await jsonBody(request)
    if (typeof body.message !== 'string' || !body.message.trim()) {
      throw 'Message is required'
    }
    const message = body.message.trim()

    const toolsEnabled = this.#toolsEnabledFrom(body)
    const toolDetails = this.#toolDetailsFrom(body)
    const temperature = this.#temperatureFrom(body)
    const targetParty = this.#targetPartyFrom(body)
    const chatId = chatIdFrom(body)
    const session = await this.#sessionForRequest(
      request,
      targetParty,
      chatId,
      undefined,
      temperature,
      this.#modelFrom(body),
    )
    clearPendingClientTools(session)
    session.toolRounds = 0
    updateClientTools(session, body)
    if (toolsEnabled) await this.#refreshToolCatalog(session)

    return await this.#withPartialEvents(events, async () => {
      const result = await this.#postParty(
        session.targetParty,
        '/ai/chat/send',
        {
          chatId: session.chatId,
          message,
          options: this.#sendOptions(session, toolsEnabled, temperature),
        },
        session.forward,
      )

      return await this.#continueConversation(
        session,
        result.message as GenerativeChatMessage,
        toolsEnabled,
        toolDetails,
        temperature,
        events,
      )
    })
  }

  /**
   * Runs a browser-facing conversation turn, returning whatever render events
   * were accumulated so far if it throws mid-flight. `events` is mutated in
   * place by the conversation recursion, so on failure it still holds the trail
   * (thinking, tool calls, tool results) already produced and persisted on the
   * platform; we append a trailing error event and return it rather than
   * discarding the lot with a bare `{ error }` from the handler. The expert path
   * deliberately does not use this — it keeps propagating real errors.
   */
  async #withPartialEvents(
    events: GenerativeChatRenderEvent[],
    run: () => Promise<GenerativeChatConversationResult>,
  ): Promise<GenerativeChatConversationResult> {
    try {
      return await run()
    } catch (e) {
      events.push({ role: 'assistant', content: String(e), error: true })
      return { events }
    }
  }

  async #clientToolResults(
    request: Request,
    events: GenerativeChatRenderEvent[],
  ): Promise<GenerativeChatConversationResult> {
    const body = await jsonBody(request)
    const toolsEnabled = this.#toolsEnabledFrom(body)
    const toolDetails = this.#toolDetailsFrom(body)
    const temperature = this.#temperatureFrom(body)
    const targetParty = this.#targetPartyFrom(body)
    const chatId = chatIdFrom(body)
    const session = await this.#sessionForRequest(
      request,
      targetParty,
      chatId,
      undefined,
      temperature,
    )
    updateClientTools(session, body)

    const clientResults = validateClientToolResults(session, body.toolResults)
    for (const result of clientResults) {
      appendToolResultEvent(
        events,
        result.name ?? 'client_tool',
        result.content,
        toolDetails,
        result.isError,
      )
    }
    const toolResults = [...session.pendingToolResults, ...clientResults]
    clearPendingClientTools(session)

    return await this.#withPartialEvents(events, () =>
      this.#continueAfterToolResults(
        session,
        toolResults,
        toolsEnabled,
        toolDetails,
        temperature,
        events,
      ))
  }

  async #continueConversation(
    session: GenerativeChatSession,
    message: GenerativeChatMessage,
    toolsEnabled: boolean,
    toolDetails: boolean,
    temperature: number,
    events: GenerativeChatRenderEvent[],
  ): Promise<GenerativeChatConversationResult> {
    const assistantMessage = message

    while (true) {
      appendAssistantEvent(events, assistantMessage)

      const toolCalls = assistantMessage.toolCalls ?? []
      if (!toolCalls.length) return { events }

      if (session.toolRounds >= this.#maxToolRounds) {
        events.push({
          role: 'tool-result',
          content: 'Stopped after too many tool rounds.',
          error: true,
        })
        return { events }
      }

      const { toolResults, clientToolCalls } = await resolveToolCalls(
        session,
        toolCalls,
        toolDetails,
        events,
      )

      if (clientToolCalls.length) {
        session.pendingClientToolCalls = new Map(
          clientToolCalls.map((call) => [call.id, call]),
        )
        session.pendingToolResults = toolResults
        return { events, clientToolCalls }
      }

      const result = await this.#continueAfterToolResults(
        session,
        toolResults,
        toolsEnabled,
        toolDetails,
        temperature,
        events,
      )
      if (result.clientToolCalls?.length) return result
      return result
    }
  }

  async #continueAfterToolResults(
    session: GenerativeChatSession,
    toolResults: ToolResult[],
    toolsEnabled: boolean,
    toolDetails: boolean,
    temperature: number,
    events: GenerativeChatRenderEvent[],
  ): Promise<GenerativeChatConversationResult> {
    if (session.toolRounds >= this.#maxToolRounds) {
      events.push({
        role: 'tool-result',
        content: 'Stopped after too many tool rounds.',
        error: true,
      })
      return { events }
    }

    session.toolRounds++
    const result = await this.#postParty(session.targetParty, '/ai/chat/send', {
      chatId: session.chatId,
      message: {
        role: 'user',
        content: '',
        toolResults,
      },
      options: this.#sendOptions(session, toolsEnabled, temperature),
    }, session.forward)

    return await this.#continueConversation(
      session,
      result.message as GenerativeChatMessage,
      toolsEnabled,
      toolDetails,
      temperature,
      events,
    )
  }

  async #getSession(
    request: Request,
  ): Promise<{
    chatId: string
    history: GenerativeChatMessage[]
    historyEvents: GenerativeChatRenderEvent[]
    closed: string | null
  }> {
    const body = await optionalJsonBody(request)
    const targetParty = this.#targetPartyFrom(body)
    const chatId = chatIdFrom(body)
    const forward = forwardContextFrom(request)
    const session = await this.#sessionForRequest(
      request,
      targetParty,
      chatId,
      chatId
        ? undefined
        : await this.#systemPromptFrom(body, targetParty, forward),
      this.#temperatureFrom(body),
      this.#modelFrom(body),
    )
    setClientTools(session, body.clientTools)
    await this.#refreshToolCatalog(session)
    const history = await this.#chatHistory(session)
    return {
      chatId: session.chatId,
      history,
      // The same render-event shape as a live ConversationResult, so a resumed
      // chat renders identically to the live exchange (reasoning + tool steps).
      historyEvents: historyToRenderEvents(history, {
        toolDetails: this.#toolDetailsFrom(body),
        clientToolNames: session.clientToolNames,
      }),
      closed: chatId ? await this.#chatClosed(targetParty, chatId, forward) : null,
    }
  }

  async #getSettings(request: Request): Promise<GenerativeChatSettings> {
    const body = await optionalJsonBody(request)
    if (Object.keys(body).length) {
      this.#updateSettings(body)
    }
    return this.#settingsWithPrompt()
  }

  /**
   * The models a chat may be created with, forwarded from the platform catalog.
   * Each entry carries its `capabilities` so a consuming app can render the
   * right controls (e.g. only offer Extended Thinking for a model that supports
   * it) and select a model at chat-creation time.
   */
  async #models(request: Request): Promise<{ models: JSONRecord[] }> {
    const body = await optionalJsonBody(request)
    const targetParty = this.#targetPartyFrom(body)
    const result = await this.#postParty(
      targetParty,
      '/ai/chat/models',
      {},
      forwardContextFrom(request),
    )
    return {
      models: Array.isArray(result.models)
        ? result.models as JSONRecord[]
        : [],
    }
  }

  async #listChats(request: Request): Promise<{ chats: JSONRecord[] }> {
    const body = await optionalJsonBody(request)
    const targetParty = this.#targetPartyFrom(body)
    const result = await this.#postParty(
      targetParty,
      '/ai/chat/list',
      {},
      forwardContextFrom(request),
    )
    return {
      chats: Array.isArray(result.chats) ? result.chats as JSONRecord[] : [],
    }
  }

  async #close(request: Request): Promise<boolean> {
    const body = await optionalJsonBody(request)
    const targetParty = this.#targetPartyFrom(body)
    const chatId = chatIdFrom(body)
    if (!chatId) throw 'chatId is required'
    const forward = forwardContextFrom(request)
    const key = sessionKey(request, targetParty, chatId)
    const session = this.#sessions.get(key)
    if (!session) {
      await this.#postParty(targetParty, '/ai/chat/close', { chatId }, forward)
      return true
    }

    await this.#postParty(session.targetParty, '/ai/chat/close', {
      chatId: session.chatId,
    }, session.forward)
    this.#sessions.delete(key)
    return true
  }

  async #deleteChat(request: Request): Promise<boolean> {
    const body = await optionalJsonBody(request)
    const targetParty = this.#targetPartyFrom(body)
    const chatId = chatIdFrom(body)
    if (!chatId) throw 'chatId is required'
    const key = sessionKey(request, targetParty, chatId)

    await this.#postParty(
      targetParty,
      '/ai/chat/delete',
      { chatId },
      forwardContextFrom(request),
    )

    this.#sessions.delete(key)
    return true
  }

  async #sessionForRequest(
    request: Request,
    targetParty: string,
    requestedChatId?: string,
    systemPrompt?: string,
    temperature = this.#defaultTemperature,
    model = this.#defaultModel,
  ): Promise<GenerativeChatSession> {
    let key = sessionKey(request, targetParty, requestedChatId)
    if (requestedChatId) {
      const existing = this.#sessions.get(key)
      if (existing) return existing
    }

    const forward = forwardContextFrom(request)
    const chat = requestedChatId
      ? { id: requestedChatId }
      : await this.#postParty(targetParty, '/ai/chat/create', {
        system: systemPrompt ??
          await this.#systemPromptForChat(targetParty, { expert: false }, forward),
        temperature,
        ...(model ? { model } : {}),
      }, forward)
    // Default the continuation forwarded on this session's cross-party gateway
    // calls to its own chat id, unless an upstream continuation is already being
    // passed through (multi-hop preserves the originating token).
    forward.continuation ??= String(chat.id)
    key = sessionKey(request, targetParty, String(chat.id))
    const session: GenerativeChatSession = {
      chatId: String(chat.id),
      targetParty,
      forward,
      toolRuntime: await this.#buildToolRuntime(targetParty, forward),
      clientTools: [],
      clientToolNames: new Set<string>(),
      pendingClientToolCalls: new Map<string, ChatToolCall>(),
      pendingToolResults: [],
      toolRounds: 0,
    }
    await this.#refreshToolCatalog(session)
    this.#sessions.set(key, session)
    return session
  }

  async #chatHistory(
    session: GenerativeChatSession,
  ): Promise<GenerativeChatMessage[]> {
    const result = await this.#postParty(
      session.targetParty,
      '/ai/chat/history',
      {
        chatId: session.chatId,
      },
      session.forward,
    )
    return Array.isArray(result.messages)
      ? result.messages as unknown as GenerativeChatMessage[]
      : []
  }

  async #chatClosed(
    targetParty: string,
    chatId: string,
    forward?: ForwardContext,
  ): Promise<string | null> {
    const result = await this.#postParty(
      targetParty,
      '/ai/chat/list',
      {},
      forward,
    )
    const chats = Array.isArray(result.chats)
      ? result.chats as JSONRecord[]
      : []
    const chat = chats.find((item) => item.id === chatId)
    return typeof chat?.closed === 'string' ? chat.closed : null
  }

  #toolRuntimeForTarget(
    targetParty: string,
    forward?: ForwardContext,
    scope?: string,
    memorySelection?: string[],
    structuredMemorySelection?: string[],
  ): AgentToolRuntime {
    return new AgentToolRuntime({
      // Inject the owner's tick selection into memory-catalog tool calls so the
      // `memory_catalog` tool sees the same ticked ∩ permitted set as the
      // system-prompt inventory. The catalog route is shared by both kinds, so
      // pick the selection matching the call's `kind` (semantic by default).
      // Other routes (and query/get) are untouched.
      postParty: (path, body) =>
        this.#postParty(
          targetParty,
          path,
          this.#withMemorySelection(path, body, memorySelection, structuredMemorySelection),
          forward,
          scope,
        ),
      postPartyRaw: (path, body) =>
        this.#postPartyRaw(targetParty, path, body, forward, scope),
    })
  }

  // For a memory-catalog call, overlay the tick selection for the call's kind as
  // `collections`; pass every other call through unchanged.
  #withMemorySelection(
    path: string,
    body: JSONRecord,
    memorySelection?: string[],
    structuredMemorySelection?: string[],
  ): JSONRecord {
    if (path !== '/ai/memory/catalog') return body
    const selection = body.kind === 'structured'
      ? structuredMemorySelection
      : memorySelection
    return selection ? { ...body, collections: selection } : body
  }

  /**
   * The owner's memory tick selection for `targetParty`, or undefined when
   * unconfigured or when the target is not the agent's own party (memories live
   * only on the own party, so cross-party providers never carry a selection).
   */
  async #memorySelection(
    targetParty: string,
    forward?: ForwardContext,
  ): Promise<string[] | undefined> {
    if (!this.#resolveMemorySelection) return undefined
    if (targetParty !== Lifecycle.getConfig().system.party) return undefined
    return await this.#resolveMemorySelection(
      targetParty,
      forward ?? { oauthContext: null, counterpartyContext: null, continuation: null },
    )
  }

  /** The structured-memory twin of {@link #memorySelection}. */
  async #structuredMemorySelection(
    targetParty: string,
    forward?: ForwardContext,
  ): Promise<string[] | undefined> {
    if (!this.#resolveStructuredMemorySelection) return undefined
    if (targetParty !== Lifecycle.getConfig().system.party) return undefined
    return await this.#resolveStructuredMemorySelection(
      targetParty,
      forward ?? { oauthContext: null, counterpartyContext: null, continuation: null },
    )
  }

  /**
   * Builds the tool runtime for a session. With no provider resolver (or an
   * empty result) this is the default single-party runtime targeting
   * `targetParty`. Otherwise it aggregates one runtime per provider, each
   * carrying the same initiator forward context so every provider authorizes
   * calls as the counterparty, exposing only the allow-listed tools.
   */
  async #buildToolRuntime(
    targetParty: string,
    forward: ForwardContext,
  ): Promise<SessionToolRuntime> {
    const providers = await this.#resolveToolProviders?.(targetParty, forward)
    if (!providers?.length) {
      return this.#toolRuntimeForTarget(
        targetParty,
        forward,
        undefined,
        await this.#memorySelection(targetParty, forward),
        await this.#structuredMemorySelection(targetParty, forward),
      )
    }

    const ownParty = Lifecycle.getConfig().system.party
    return new AggregateToolRuntime(
      await Promise.all(providers.map(async (provider) => ({
        key: provider.party,
        // Same-party providers use the direct platform endpoints (which include
        // memory tools, and carry the owner's tick selection); cross-party
        // providers go through the member's MCP gateway with a signed token +
        // forwarded initiator context, and never surface memories.
        runtime: provider.party === ownParty
          ? this.#toolRuntimeForTarget(
            provider.party,
            forward,
            provider.scope,
            await this.#memorySelection(provider.party, forward),
            await this.#structuredMemorySelection(provider.party, forward),
          )
          : this.#gatewayRuntimeForTarget(provider.party, forward),
        allowedTools: provider.tools ? new Set(provider.tools) : undefined,
      }))),
    )
  }

  #gatewayRuntimeForTarget(
    targetParty: string,
    forward?: ForwardContext,
  ): GatewayToolRuntime {
    return new GatewayToolRuntime({
      postRpc: (body) => this.#postPartyRaw(targetParty, '/mcp/rpc', body, forward),
    })
  }

  async #refreshToolCatalog(session: GenerativeChatSession): Promise<void> {
    await session.toolRuntime.refreshCatalog()
  }

  #sendOptions(
    session: GenerativeChatSession,
    toolsEnabled: boolean,
    temperature: number,
  ): JSONRecord | undefined {
    const thinking = this.#settings.thinking
    const base: JSONRecord = { temperature, thinking }
    if (!toolsEnabled) return base

    const tools = session.toolRuntime.chatTools({
      clientTools: session.clientTools,
    })
    if (!tools.length) return base

    return {
      ...base,
      tools,
      toolChoice: 'auto',
    }
  }

  async #postParty(
    targetParty: string,
    path: string,
    body: JSONRecord,
    forward?: ForwardContext,
    scope?: string,
  ): Promise<JSONRecord> {
    const json = await this.#postPartyRaw(targetParty, path, body, forward, scope)
    if ('error' in json) throw json.error
    return json.ok as JSONRecord
  }

  async #postPartyRaw(
    targetParty: string,
    path: string,
    body: JSONRecord,
    forward?: ForwardContext,
    scope?: string,
  ): Promise<JSONRecord> {
    const config = Lifecycle.getConfig()
    const token = await Lifecycle.getTokenFor(
      { [config.system.source]: scope ?? this.#targetScope },
      targetParty,
    )
    const url = `${config.system.protocol}//${targetParty}${path}`
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Tabserver-Token': token.asSignedBase64(),
    }
    if (forward?.oauthContext) {
      headers[OAUTH_CONTEXT_HEADER] = forward.oauthContext
    }
    if (forward?.counterpartyContext) {
      headers[COUNTERPARTY_CONTEXT_HEADER] = forward.counterpartyContext
    }
    if (forward?.continuation) {
      headers[CONTINUATION_HEADER] = forward.continuation
    }
    const result = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    })
    return await result.json()
  }

  async #systemPromptFrom(
    body: JSONRecord,
    targetParty: string,
    forward?: ForwardContext,
  ): Promise<string> {
    const basePrompt = typeof body.systemPrompt === 'string' &&
        body.systemPrompt.trim()
      ? body.systemPrompt.trim()
      : this.#settingsSystemPrompt()
    return await this.#systemPromptForChat(targetParty, {
      basePrompt,
      expert: false,
    }, forward)
  }

  #modelFrom(body: JSONRecord): string | undefined {
    return typeof body.model === 'string' && body.model.trim()
      ? body.model.trim()
      : undefined
  }

  #temperatureFrom(body: JSONRecord): number {
    return typeof body.temperature === 'number' &&
        Number.isFinite(body.temperature) &&
        body.temperature >= 0 &&
        body.temperature <= 2
      ? Math.round(body.temperature * 10) / 10
      : this.#settings.temperature
  }

  #toolsEnabledFrom(body: JSONRecord): boolean {
    return typeof body.toolsEnabled === 'boolean'
      ? body.toolsEnabled
      : this.#settings.toolsEnabled
  }

  #toolDetailsFrom(body: JSONRecord): boolean {
    return typeof body.toolDetails === 'boolean'
      ? body.toolDetails
      : this.#settings.toolDetails
  }

  #targetPartyFrom(body: JSONRecord): string {
    return this.#normalizeTargetParty(body.targetParty)
  }

  #normalizeTargetParty(value: unknown): string {
    const {
      system: {
        party,
        protocol,
      },
    } = Lifecycle.getConfig()
    if (value === undefined || value === null || value === '') return party
    if (typeof value !== 'string') throw 'targetParty must be a string'

    const trimmed = value.trim()
    if (!trimmed) return party
    try {
      const url = trimmed.includes('://')
        ? new URL(trimmed)
        : new URL(`${protocol}//${trimmed}`)
      if (!url.host) throw 'missing host'
      return url.host
    } catch (_e) {
      throw `Invalid targetParty: ${value}`
    }
  }

  async #systemPromptForChat(
    targetParty: string,
    options: { basePrompt?: string; expert: boolean },
    forward?: ForwardContext,
  ): Promise<string> {
    const basePrompt = options.basePrompt ?? this.#settingsSystemPrompt()
    const root = await expertRoot()
    const expertInstruction = options.expert && root
      ? `\nWhen answering expert MCP questions, prefer semantic and structured memory under root "${root}" when looking up remembered information.`
      : ''
    const semanticInventory = await this.#semanticMemoryPrompt(targetParty, forward)
    const structuredInventory = await this.#structuredMemoryPrompt(targetParty, forward)
    return `${basePrompt}${expertInstruction}${semanticInventory}${structuredInventory}`
  }

  async #semanticMemoryPrompt(
    targetParty: string,
    forward?: ForwardContext,
  ): Promise<string> {
    try {
      const collections = await this.#memorySelection(targetParty, forward)
      const configuredRoot = configuredMemoryRoot()
      const resources = configuredRoot
        ? await this.#semanticMemoryResourcesForRootWithFallback(
          targetParty,
          configuredRoot,
          forward,
          collections,
        )
        : await this.#semanticMemoryResources(targetParty, { collections }, forward)
      return semanticMemoryPromptSection(resources)
    } catch (_e) {
      return '\n\nSemantic memory catalog is unavailable for this session.'
    }
  }

  async #semanticMemoryResourcesForRootWithFallback(
    targetParty: string,
    root: string,
    forward?: ForwardContext,
    collections?: string[],
  ): Promise<SemanticMemoryPromptResource[]> {
    const rooted = await this.#semanticMemoryResources(
      targetParty,
      { root, collections },
      forward,
    )
    return rooted.length ? rooted : await this.#semanticMemoryResources(
      targetParty,
      { collections },
      forward,
    )
  }

  async #semanticMemoryResources(
    targetParty: string,
    options: { root?: string; collections?: string[] },
    forward?: ForwardContext,
  ): Promise<SemanticMemoryPromptResource[]> {
    const result = await this.#postParty(targetParty, '/ai/memory/catalog', {
      kind: 'semantic',
      ...(options.root ? { root: options.root } : {}),
      ...(options.collections ? { collections: options.collections } : {}),
    }, forward)
    return Array.isArray(result.resources)
      ? result.resources as SemanticMemoryPromptResource[]
      : []
  }

  // Structured twin of #semanticMemoryPrompt: the same catalog surface and root
  // fallback, keyed off the structured tick selection and rendered as a
  // structured_query inventory.
  async #structuredMemoryPrompt(
    targetParty: string,
    forward?: ForwardContext,
  ): Promise<string> {
    try {
      const collections = await this.#structuredMemorySelection(targetParty, forward)
      const configuredRoot = configuredMemoryRoot()
      const rooted = configuredRoot
        ? await this.#structuredMemoryResources(targetParty, { root: configuredRoot, collections }, forward)
        : []
      const resources = configuredRoot && rooted.length
        ? rooted
        : await this.#structuredMemoryResources(targetParty, { collections }, forward)
      return structuredMemoryPromptSection(resources)
    } catch (_e) {
      return '\n\nStructured memory catalog is unavailable for this session.'
    }
  }

  async #structuredMemoryResources(
    targetParty: string,
    options: { root?: string; collections?: string[] },
    forward?: ForwardContext,
  ): Promise<SemanticMemoryPromptResource[]> {
    const result = await this.#postParty(targetParty, '/ai/memory/catalog', {
      kind: 'structured',
      ...(options.root ? { root: options.root } : {}),
      ...(options.collections ? { collections: options.collections } : {}),
    }, forward)
    return Array.isArray(result.resources)
      ? result.resources as SemanticMemoryPromptResource[]
      : []
  }

  #settingsSystemPrompt(): string {
    return this.#settings.systemPrompt ?? this.#defaultSystemPrompt
  }

  #settingsWithPrompt(): GenerativeChatSettings {
    return {
      ...this.#settings,
      systemPrompt: this.#settingsSystemPrompt(),
    }
  }

  #updateSettings(body: JSONRecord): void {
    if (body.systemPrompt !== undefined) {
      if (typeof body.systemPrompt !== 'string') {
        throw 'systemPrompt must be a string'
      }
      this.#settings.systemPrompt = body.systemPrompt.trim()
        ? body.systemPrompt
        : this.#defaultSystemPrompt
    }
    if (body.temperature !== undefined) {
      if (
        typeof body.temperature !== 'number' ||
        !Number.isFinite(body.temperature) ||
        body.temperature < 0 ||
        body.temperature > 2
      ) {
        throw 'temperature must be between 0 and 2'
      }
      this.#settings.temperature = Math.round(body.temperature * 10) / 10
    }
    if (body.toolsEnabled !== undefined) {
      if (typeof body.toolsEnabled !== 'boolean') {
        throw 'toolsEnabled must be a boolean'
      }
      this.#settings.toolsEnabled = body.toolsEnabled
    }
    if (body.toolDetails !== undefined) {
      if (typeof body.toolDetails !== 'boolean') {
        throw 'toolDetails must be a boolean'
      }
      this.#settings.toolDetails = body.toolDetails
    }
    if (body.thinking !== undefined) {
      if (typeof body.thinking !== 'boolean') {
        throw 'thinking must be a boolean'
      }
      this.#settings.thinking = body.thinking
    }
  }

}

async function jsonBody(request: Request): Promise<JSONRecord> {
  if (!request.headers.get('Content-Type')?.startsWith('application/json')) {
    throw "Content-Type must be 'application/json'"
  }
  const body = await request.json()
  if (!body || typeof body !== 'object' || Array.isArray(body)) {
    throw 'Request body must be an object'
  }
  return body as JSONRecord
}

async function optionalJsonBody(request: Request): Promise<JSONRecord> {
  if (!request.headers.get('Content-Type')?.startsWith('application/json')) {
    return {}
  }
  const body = await request.json()
  if (!body || typeof body !== 'object' || Array.isArray(body)) return {}
  return body as JSONRecord
}

function updateClientTools(
  session: GenerativeChatSession,
  body: JSONRecord,
): void {
  if (Object.hasOwn(body, 'clientTools')) {
    setClientTools(session, body.clientTools)
  }
}

function setClientTools(
  session: GenerativeChatSession,
  value: unknown,
): void {
  const clientTools = Array.isArray(value)
    ? value.filter(isClientToolDescriptor)
    : []
  session.clientTools = clientTools
  session.clientToolNames = new Set(clientTools.map((tool) => tool.name))
}

function isClientToolDescriptor(value: unknown): value is ClientToolDescriptor {
  return (
    Boolean(value) &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    (value as { type?: unknown }).type === 'function' &&
    typeof (value as { name?: unknown }).name === 'string' &&
    (value as { execution?: unknown }).execution === 'client'
  )
}

function validateClientToolResults(
  session: GenerativeChatSession,
  value: unknown,
): ToolResult[] {
  if (!Array.isArray(value)) throw 'toolResults must be an array'

  const pendingIds = new Set(session.pendingClientToolCalls.keys())
  if (!pendingIds.size) throw 'No pending client tool calls'

  const results: ToolResult[] = []
  for (const item of value) {
    if (!isToolResult(item)) throw 'Invalid client tool result'
    if (!pendingIds.delete(item.toolCallId)) {
      throw `Unexpected client tool result: ${item.toolCallId}`
    }
    const pendingCall = session.pendingClientToolCalls.get(item.toolCallId)
    if (pendingCall?.name !== item.name) {
      throw `Client tool result name mismatch: ${item.toolCallId}`
    }
    results.push(item)
  }

  if (pendingIds.size) {
    throw `Missing client tool results: ${[...pendingIds].join(', ')}`
  }

  return results
}

function isToolResult(value: unknown): value is ToolResult {
  return (
    Boolean(value) &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    typeof (value as { toolCallId?: unknown }).toolCallId === 'string' &&
    typeof (value as { name?: unknown }).name === 'string' &&
    typeof (value as { content?: unknown }).content === 'string' &&
    (
      (value as { isError?: unknown }).isError === undefined ||
      typeof (value as { isError?: unknown }).isError === 'boolean'
    )
  )
}

function clearPendingClientTools(session: GenerativeChatSession): void {
  session.pendingClientToolCalls.clear()
  session.pendingToolResults = []
}

function chatIdFrom(body: JSONRecord): string | undefined {
  if (body.chatId === undefined || body.chatId === null || body.chatId === '') {
    return undefined
  }
  if (typeof body.chatId !== 'string') throw 'chatId must be a string'
  return body.chatId
}

function sessionKey(
  request: Request,
  targetParty: string,
  chatId?: string,
): string {
  return `${requestIdentity(request)}\n${targetParty}\n${chatId ?? 'default'}`
}

/**
 * Reuse key for grouping repeated `expert_ask` calls into one bar-side chat.
 * The caller-chosen `continuation` token is namespaced under the caller's
 * *verified* token identity (`sub`+`src`), so a caller can only ever address its
 * own continuation tokens — a different caller sending the same token cannot
 * collide with or hijack this caller's chat. Returns null when no verifiable
 * identity is present, in which case the caller falls back to stateless behavior.
 */
function expertReuseKey(request: Request, continuation: string): string | null {
  try {
    const token = Token.from(request)
    if (!token) return null
    return `${token.getSub()}\n${token.getSrc()}\n${continuation}`
  } catch (_e) {
    return null
  }
}

function requestIdentity(request: Request): string {
  const token = request.headers.get(TOKEN_HEADER)
  if (token) return token
  const oauthContext = request.headers.get(OAUTH_CONTEXT_HEADER)
  if (oauthContext) return oauthContext
  throw `This request must provide a token in '${TOKEN_HEADER}'`
}

/**
 * Derive the identity to forward downstream from the request the agent
 * authenticated at hop-1. OAuth identity travels via the OAuth context header
 * (the target derives the counterparty from it); for a signed-token peer we
 * forward `{counterparty, src}` so the target can authorize as that peer.
 */
function forwardContextFrom(request: Request): ForwardContext {
  const oauthContext = request.headers.get(OAUTH_CONTEXT_HEADER)
  return {
    oauthContext,
    counterpartyContext: counterpartyContextFrom(request, oauthContext),
    // Pass an inbound continuation through unchanged (multi-hop); otherwise it is
    // defaulted to the new chat id in `#sessionForRequest` once that is known.
    continuation: request.headers.get(CONTINUATION_HEADER),
  }
}

function counterpartyContextFrom(
  request: Request,
  oauthContext: string | null,
): string | null {
  const existing = request.headers.get(COUNTERPARTY_CONTEXT_HEADER)
  if (existing) return existing
  // OAuth identity is forwarded via OAUTH_CONTEXT_HEADER and derived downstream.
  if (oauthContext) return null
  try {
    const token = Token.from(request)
    if (!token) return null
    return JSON.stringify({
      counterparty: token.getCounterparty(),
      src: token.getSrc(),
    })
  } catch (_e) {
    // An opaque/unparseable hop-1 token yields no counterparty assertion; the
    // target then authorizes against the agent alone (no escalation).
    return null
  }
}

async function resolveToolCalls(
  session: GenerativeChatSession,
  toolCalls: ChatToolCall[],
  toolDetails: boolean,
  events: GenerativeChatRenderEvent[],
): Promise<{ toolResults: ToolResult[]; clientToolCalls: ChatToolCall[] }> {
  const toolResults: ToolResult[] = []
  const clientToolCalls: ChatToolCall[] = []

  for (const call of toolCalls) {
    const isClientTool = session.clientToolNames.has(call.name)
    if (toolDetails) {
      events.push({
        role: 'tool',
        content: toolCallMessage(call, isClientTool),
      })
    }

    if (isClientTool) {
      clientToolCalls.push(call)
      continue
    }

    try {
      const content = await session.toolRuntime.executeToolCall(call)
      appendToolResultEvent(events, call.name, content, toolDetails)
      toolResults.push({
        toolCallId: call.id,
        name: call.name,
        content,
      })
    } catch (e) {
      const content = String(e)
      appendToolResultEvent(events, call.name, content, toolDetails, true)
      toolResults.push({
        toolCallId: call.id,
        name: call.name,
        content,
        isError: true,
      })
    }
  }

  return { toolResults, clientToolCalls }
}

class GenerativeChatExpertTools
  implements McpToolProvider<GenerativeChatMcpContext> {
  #agent: GenerativeChatAgent

  constructor(agent: GenerativeChatAgent) {
    this.#agent = agent
  }

  listTools(): JSONValue[] {
    return [expertAskDescriptor()]
  }

  async callTool(
    name: string,
    args: McpToolArguments,
    context: GenerativeChatMcpContext,
  ): Promise<JSONValue> {
    if (name !== EXPERT_ASK) {
      throw new ProtocolError(-32602, `Unknown tool: ${name}`)
    }
    assertExpertAskCapability(context)
    if (typeof args.question !== 'string' || !args.question.trim()) {
      throw 'question is required'
    }
    return await this.#agent.askExpert(context.request, args.question)
  }
}

function expertAskDescriptor(): JSONValue {
  return {
    name: EXPERT_ASK,
    title: 'Ask Expert',
    description:
      'Ask this AI Chat expert a question. The expert may use its configured memory and tools before answering.',
    inputSchema: {
      type: 'object',
      properties: {
        question: {
          type: 'string',
          description: 'Question to ask the expert.',
        },
      },
      required: ['question'],
      additionalProperties: false,
    },
    outputSchema: {
      type: 'object',
      properties: {
        answer: {
          type: 'string',
        },
      },
      required: ['answer'],
      additionalProperties: false,
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
    },
  }
}

function assertExpertAskCapability(context: GenerativeChatMcpContext): void {
  if (
    !hasMcpToolCapability({
      token: context.token,
      providerSource: context.source,
      toolName: EXPERT_ASK,
      capability: EXPERT_ASK_CAPABILITY,
      providerYaml: context.yaml,
    })
  ) {
    throw `Missing capability: ${EXPERT_ASK_CAPABILITY}`
  }
}

function lifecycleContext(request: Request): GenerativeChatMcpContext {
  const {
    system: {
      party,
      source,
      protocol,
      sourcePrefix,
    },
    content: {
      yaml,
    },
  } = Lifecycle.getConfig()

  return {
    party,
    source,
    protocol,
    sourcePrefix,
    yaml,
    token: mcpTokenFromRequest(request),
    request,
  }
}

function mcpTokenFromRequest(
  request: Request,
): Token | McpCapabilityToken | null {
  const token = Token.from(request)
  if (token) return token

  const json = request.headers.get(OAUTH_CONTEXT_HEADER)
  if (!json) return null
  return new OAuthMcpToken(JSON.parse(json))
}

function latestAssistantAnswer(
  events: GenerativeChatRenderEvent[],
): string | null {
  for (let index = events.length - 1; index >= 0; index--) {
    const event = events[index]
    if (event.role === 'assistant' && !event.error) {
      return event.content
    }
  }
  return null
}

function semanticMemoryPromptSection(
  resources: SemanticMemoryPromptResource[],
): string {
  if (!resources.length) {
    return '\n\nAvailable semantic memories:\nNone discovered.'
  }

  const lines = [
    '',
    '',
    'Available semantic memories:',
    'Use semantic_query with the exact root and collection when these memories are relevant.',
  ]
  for (const resource of resources) {
    const root = stringValue(resource.root)
    const collection = stringValue(resource.collection)
    if (!root || !collection) continue
    lines.push(`- root: ${root}`)
    lines.push(`  collection: ${collection}`)
    const description = stringValue(resource.description)
    if (description) lines.push(`  description: ${description}`)
    const schema = objectValue(resource.schema)
    const textProperties = stringArrayValue(schema?.textProperties)
    if (textProperties.length) {
      lines.push(`  textProperties: ${textProperties.join(', ')}`)
    }
    const propertyTypes = objectValue(schema?.propertyTypes)
    if (propertyTypes) {
      lines.push(`  propertyTypes: ${JSON.stringify(propertyTypes)}`)
    }
    lines.push(
      `  query with: semantic_query({ root: ${
        JSON.stringify(root)
      }, collection: ${
        JSON.stringify(collection)
      }, query: { text: "<search text>", limit: 5 } })`,
    )
  }

  return lines.join('\n')
}

function structuredMemoryPromptSection(
  resources: SemanticMemoryPromptResource[],
): string {
  if (!resources.length) {
    return '\n\nAvailable structured memories:\nNone discovered.'
  }

  const lines = [
    '',
    '',
    'Available structured memories:',
    'Use structured_query with the exact root and collection when these memories are relevant.',
  ]
  for (const resource of resources) {
    const root = stringValue(resource.root)
    const collection = stringValue(resource.collection)
    if (!root || !collection) continue
    lines.push(`- root: ${root}`)
    lines.push(`  collection: ${collection}`)
    const description = stringValue(resource.description)
    if (description) lines.push(`  description: ${description}`)
    const schema = objectValue(resource.schema)
    const textProperties = stringArrayValue(schema?.textProperties)
    if (textProperties.length) {
      lines.push(`  textProperties: ${textProperties.join(', ')}`)
    }
    const propertyTypes = objectValue(schema?.propertyTypes)
    if (propertyTypes) {
      lines.push(`  propertyTypes: ${JSON.stringify(propertyTypes)}`)
    }
    lines.push(
      `  query with: structured_query({ root: ${
        JSON.stringify(root)
      }, collection: ${
        JSON.stringify(collection)
      }, query: { limit: 5 } })`,
    )
  }

  return lines.join('\n')
}

function stringValue(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function objectValue(value: unknown): JSONRecord | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as JSONRecord
    : null
}

function stringArrayValue(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : []
}

function configuredMemoryRoot(): string | undefined {
  const {
    system: { source },
  } = Lifecycle.getConfig()
  const sourceUrl = new URL(source)
  const root = sourceUrl.searchParams.get('root')?.trim()
    .replace(/^\/+|\/+$/g, '')
  return root || undefined
}

async function expertRoot(): Promise<string> {
  const {
    system: { source },
  } = Lifecycle.getConfig()
  const sourceUrl = new URL(source)
  const configuredRoot = configuredMemoryRoot()
  if (configuredRoot) return configuredRoot
  sourceUrl.hash = ''
  return await getAppId(sourceUrl)
}
